import type { BorescopeInspectionEvent } from '@avisoma/shared';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import { formatBorescopeClock, formatBorescopeDate, formatOverallResult, summariseFindings } from './borescopePresentation';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

/**
 * Dense grid for consolidated borescope / inspection events (DR-4817).
 *
 * Like the workscope table a row carries `pageIds` (plural): consolidation groups the header
 * and stage pages of one report into a single event, so the source column lists each
 * contributing page. The finding rows are summarised (count and first areas); the full rows,
 * the `dateRaw` / `dateLabel` evidence lanes, the issuer's `organisationLocation` and the
 * `dateReading` are read from the row inspector. The Date cell names a non-trivial reading and
 * shows an unresolved date's printed token (DR-5443).
 */
export function BorescopeTable({
  rows,
  pageIdToPosition,
  onInspect,
  rowFindings,
}: {
  rows: BorescopeInspectionEvent[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: BorescopeInspectionEvent, index: number) => void;
  rowFindings?: RowFindings;
}) {
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Pages</Th>}
          <Th>Subject</Th>
          <Th>ESN</Th>
          <Th>Date</Th>
          <Th>Work Order</Th>
          <Th>Report Ref</Th>
          <Th>Overall Result</Th>
          <Th>Findings</Th>
          <Th>TSN</Th>
          <Th>CSN</Th>
          <Th>Organisation</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((row, index) => {
          // `pageIds` is a jsonb-backed array — a row written mid-reprocessing can reach the UI
          // without it, so never map over it unguarded.
          const pageIds = row.pageIds ?? [];
          return (
            <tr
              key={`${row.workOrder ?? row.reportRef ?? 'inspection'}-${pageIds[0] ?? 'page'}-${index}`}
              onClick={() => onInspect(row, index)}
              className={extractionRowClass}
            >
              {rowFindings && <FindingMarkerCell findings={rowFindings.get(index)} />}
              {pageIdToPosition && (
                <Td>
                  {pageIds.length > 0 ? (
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                      {pageIds.map((pageId) => (
                        <SourcePageCell key={pageId} pageId={pageId} pageIdToPosition={pageIdToPosition} />
                      ))}
                    </div>
                  ) : (
                    '—'
                  )}
                </Td>
              )}
              <TruncatedTd value={row.subject} />
              <Td className="font-mono">{row.esn ?? '—'}</Td>
              <TruncatedTd value={formatBorescopeDate(row)} />
              <Td className="font-mono">{row.workOrder ?? '—'}</Td>
              <Td className="font-mono">{row.reportRef ?? '—'}</Td>
              <TruncatedTd value={formatOverallResult(row)} />
              <TruncatedTd value={summariseFindings(row)} />
              <Td>{formatBorescopeClock(row.tsn) ?? '—'}</Td>
              <Td>{formatBorescopeClock(row.csn) ?? '—'}</Td>
              <TruncatedTd value={row.organisation} />
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
